import { createSelector } from "reselect"
import { get, chain, map, filter, first, mergeWith, some, size } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { computeFilteredGroupedContributors, computeSelectedGroupSiblings, computeSelectedGroupValue, computeSortedContributors, computeSortedGroupContributors, positionSetContributorsSummaryText } from "helpers/contributors"
import { getSelectedViewsFactors } from "selectors/factors"
import { getViewType, getPortfolioName, getShowNotHeld, getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { getScreenName } from "modules/analyze-historical-exposure/selectors/file"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateExposuresFactors } from "helpers/factorClassification"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { METRIC_TYPES } from "helpers/assets/constants"

const EMPTY_OBJECT = {}

function getFactors(factors, mustAggregateFactors, styleAggregation, selectedViewFactors, factorsClassification, exposureField) {
  if (!mustAggregateFactors) {
    return factors
  }

  let aggregatedFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, exposureField)
  if (size(selectedViewFactors)) {
    aggregatedFactors = chain(factors)
      .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
      .concat(aggregatedFactors)
      .value()
  }
  return aggregatedFactors
}

function computeNotHeldBucket(contributors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureField) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity
      const factors = getFactors(value.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureField)
      merge(acc.factors, chain(factors)
        .keyBy("id")
        .mapValues(f => get(f, exposureField))
        .value())

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      factors: {},
    })
    .value()
}

function mapExposureContributors(data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureUnit) {
  const exposureField = exposureUnit === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"
  const result = chain(data)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureField)
      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        assetSubclass: security.assetSubclass,
        notHeld: security.notHeld,
        size: security.percentEquity,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => get(f, exposureField))
          .value(),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(data, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureField))
  }
  return result
}

export const getGroupBy = (_, props) => get(props, "contributorsGrouping.id")

const getContributorsData = state => get(state, "global.data.exposureContributors.data.model.simulation.exposureContributors.0.contributors")
const getGroupedContributorsData = state => get(state, "global.data.groupedExposureContributors.data.model.simulation.groupedExposureContributors.0.groupedContributors")
const getSelected = state => get(state, "modules.analyzeExposureDetails.selected", EMPTY_OBJECT)
export const getContributorsGrouping = (_, props) => get(props, "contributorsGrouping")
const getRawSelectedGroup = (_, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getOrder = state => get(state, "modules.analyzeExposureDetails.order", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (_, props) => props.selectedDate
const getExposureUnit = (_, props) => get(props, "metricType")

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getGroupedContributorsData],
  computeSelectedGroup,
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

export const getSelectedField = createSelector(
  [getSelected],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getStyleExposureAggregation, getSelectedViewsFactors, getModelFactorsClassification, getExposureUnit],
  mapExposureContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => get(security, selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  computeSortedContributors,
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getStyleExposureAggregation, getSelectedViewsFactors, getModelFactorsClassification, getExposureUnit],
  (data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureUnit) => {
    const exposureField = exposureUnit === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"
    return map(data, group => {
      const factors = getFactors(group.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureField)
      return {
        ...group,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => get(f, exposureField))
          .value(),
        contributors: mapExposureContributors(group.contributors, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, exposureUnit),
      }
    })
  },
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  computeFilteredGroupedContributors,
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  computeSortedGroupContributors,
)

const getAccountRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories],
  (tagCategories, classifications, attributeCategories) => ({
    tagCategories,
    classifications,
    attributeCategories,
  }),
)

export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getViewType, getSelectedGroup, getScreenName],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup, screenName) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: screenName,
        portfolioName,
        selected,
        selectedDate,
        viewType,
        group,
        net: true,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: screenName,
        portfolioName,
        selected,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
        net: true,
      })
    }

    return computeContributorsCSV({
      type: screenName,
      portfolioName,
      selected,
      selectedDate,
      viewType,
      data,
      net: true,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupValue,
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupSiblings,
)
